import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useRestaurantContext } from './RestaurantContext';
import { createApiProvider } from '../config/apiConfig';

// Firestore onSnapshot for real-time push (trigger only — data comes from REST)
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
} from 'firebase/firestore';

const OrderContext = createContext();
export const useOrderContext = () => useContext(OrderContext);

const api = createApiProvider();

// ─── Firestore trigger listener ───────────────────────────────────────────────
// Listens to private_restaurants/{restaurantId}/orders_collection
// filtered by firebaseUid. When a doc changes (e.g. kitchen updates status),
// we call the REST API to get the fresh authoritative data from MongoDB.

const listenToUserOrdersFirestore = (restaurantId, firebaseUid, onChangedOrderId) => {
  try {
    const db = getFirestore();
    const ordersRef = collection(db, 'restaurants', restaurantId, 'clientDisplayOrders');
    // We assume the schema of `clientDisplayOrders` now includes `firebaseUid` as added previously so this query continues to work
    const q = query(ordersRef, where('firebaseUid', '==', firebaseUid));

    let isInitialSnapshot = true;
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Skip the initial snapshot to avoid hammering the server with getSelfOrderById calls
      // for every existing order. Authoritative data is fetched via REST on mount.
      if (isInitialSnapshot) {
        isInitialSnapshot = false;
        return;
      }

      snapshot.docChanges().forEach((change) => {
        if (change.type === 'modified' || change.type === 'added') {
          const orderId = change.doc.id;
          onChangedOrderId(orderId);
        }
      });
    }, (error) => {
      console.error('Firestore order listener error:', error);
    });

    return unsubscribe;
  } catch (error) {
    console.error('Failed to set up Firestore order listener:', error);
    return () => {};
  }
};

// ─────────────────────────────────────────────────────────────────────────────

export const OrderProvider = ({ children }) => {
  const { currentUser, isAuthLoading } = useAuth();
  const { restaurantId } = useRestaurantContext();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  const [error, setError] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  
  // Pagination and Filter State
  const [pagination, setPagination] = useState({ totalPages: 0, currentPage: 1, hasMore: false, totalCount: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilterState] = useState(null); // e.g. { type: 'today' | 'yesterday' | '7days' | 'custom', date?: string, startDate?: string, endDate?: string }
  const [isHistoryMode, setIsHistoryMode] = useState(false);

  // ── Fetch orders from REST API ──────────────────────────────────────────
  const fetchOrders = useCallback(async (params = {}) => {
    if (isAuthLoading || !restaurantId || !currentUser) return;

    const isInitial = !params.page || params.page === 1;
    if (isInitial) {
      setIsLoading(true);
    } else {
      setIsMoreLoading(true);
    }

    const currentSearch = params.search !== undefined ? params.search : searchQuery;
    const currentDateFilter = params.dateFilter !== undefined ? params.dateFilter : dateFilter;
    const currentTimeRange = params.timeRange !== undefined ? params.timeRange : '';

    const fetchParams = {
      limit: 20,
      page: params.page || 1,
      search: currentSearch,
      timeRange: currentTimeRange,
      date: currentDateFilter?.date || '',
      startDate: currentDateFilter?.startDate || '',
      endDate: currentDateFilter?.endDate || ''
    };

    return new Promise((resolve) => {
      api.getOrdersByUserId(currentUser.uid, restaurantId, (result) => {
        if (result.success) {
          if (fetchParams.page > 1) {
            setOrders((prev) => {
              // Deduplicate by ID
              const existingIds = new Set(prev.map(o => o.id));
              const newOrders = (result.data || []).filter(o => !existingIds.has(o.id));
              return [...prev, ...newOrders];
            });
          } else {
            setOrders(result.data || []);
          }
          setPagination(result.pagination || { totalPages: 1, currentPage: 1, hasMore: false, totalCount: (result.data || []).length });
          setError(null);
        } else {
          if (isInitial) setOrders([]);
          setError(result.error);
        }
        setIsLoading(false);
        setIsMoreLoading(false);
        resolve(result);
      }, fetchParams);
    });
  }, [currentUser, isAuthLoading, restaurantId, searchQuery, dateFilter]);

  // Initial load
  useEffect(() => {
    if (currentUser && restaurantId && !isAuthLoading) {
      fetchOrders({ page: 1 });
    }
  }, [currentUser, restaurantId, isAuthLoading]);

  // Load more for pagination
  const loadMore = useCallback(() => {
    if (pagination.hasMore && !isMoreLoading) {
      fetchOrders({ page: pagination.currentPage + 1 });
    }
  }, [pagination, isMoreLoading, fetchOrders]);

  // Switch to history mode (all orders)
  const loadHistory = useCallback(() => {
    setIsHistoryMode(true);
    setSearchQuery('');
    setDateFilterState(null);
    fetchOrders({ page: 1, timeRange: '', search: '', dateFilter: null });
  }, [fetchOrders]);

  // Search orders
  const searchOrders = useCallback((query) => {
    setSearchQuery(query);
    setIsHistoryMode(true);
    fetchOrders({ page: 1, search: query, timeRange: '' });
  }, [fetchOrders]);

  // Set date filter
  const setDateFilter = useCallback((filter) => {
    setDateFilterState(filter);
    setIsHistoryMode(true);
    fetchOrders({ page: 1, dateFilter: filter, search: searchQuery, timeRange: '' });
  }, [fetchOrders, searchQuery]);

  // Reset to initial state (all orders / clean state)
  const resetToRecent = useCallback(() => {
    setIsHistoryMode(false);
    setSearchQuery('');
    setDateFilterState(null);
    fetchOrders({ page: 1, timeRange: '', search: '', dateFilter: null });
  }, [fetchOrders]);

  // ── Firestore onSnapshot → fetch updated order from REST ───────────────────
  const handleFirestoreOrderChange = useCallback(async (orderId) => {
    if (!restaurantId) return;
    const result = await api.getSelfOrderById(restaurantId, orderId);
    if (result.success) {
      setOrders((prev) => {
        const idx = prev.findIndex((o) => o.id === orderId);
        if (idx > -1) {
          const updated = [...prev];
          updated[idx] = { ...result.data, created_at: new Date(result.data.createdAt) };
          return updated;
        }
        return [{ ...result.data, created_at: new Date(result.data.createdAt) }, ...prev];
      });
    }
  }, [restaurantId]);

  useEffect(() => {
    if (!currentUser || !restaurantId || isAuthLoading) return;

    const unsubscribe = listenToUserOrdersFirestore(
      restaurantId,
      currentUser.uid,
      handleFirestoreOrderChange
    );

    return () => unsubscribe();
  }, [currentUser, restaurantId, isAuthLoading, handleFirestoreOrderChange]);

  // ── Live status banner ─────────────────────────────────────────────────────
  useEffect(() => {
    if (orders.length === 0) { setLiveStatus(null); return; }

    const getStatusMessage = (status) => {
      switch (status?.toLowerCase()) {
        case 'pending':
          return 'Order placed • Waiting for restaurant approval';
        case 'confirmed':
          return 'Order accepted • Preparing soon';
        case 'preparing':
        case 'processing':
          return 'Order is being prepared in kitchen';
        case 'ready_for_pickup':
          return 'Your order is ready for pickup!';
        case 'out_for_delivery':
          return 'Your order is out for delivery!';
        default:
          return `Your order is ${status}`;
      }
    };

    const activeOrder = orders.find((o) =>
      ['pending', 'confirmed', 'preparing', 'processing'].includes(o.orderStatus)
    );
    if (activeOrder) {
      setLiveStatus({
        id: activeOrder.id,
        status: activeOrder.orderStatus,
        message: getStatusMessage(activeOrder.orderStatus)
      });
      return;
    }

    const readyOrder = orders.find((o) => ['ready_for_pickup', 'out_for_delivery'].includes(o.orderStatus));
    if (readyOrder) {
      setLiveStatus({
        id: readyOrder.id,
        status: readyOrder.orderStatus,
        message: getStatusMessage(readyOrder.orderStatus)
      });
      return;
    }

    setLiveStatus(null);
  }, [orders]);

  const value = { 
    orders, 
    isLoading, 
    isMoreLoading,
    error, 
    liveStatus, 
    pagination,
    isHistoryMode,
    searchQuery,
    dateFilter,
    fetchOrders,
    loadMore,
    loadHistory,
    searchOrders,
    setDateFilter,
    resetToRecent
  };

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>;
};